import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Auth} from '../actions/auth.actions';
import {tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {PaymentProviderConfiguration} from '../models/payments/paymentProviderConfiguration.model';
import {Payments} from '../actions/payments.actions';
import {PaymentsService} from '../services/payments.service';
import {JobPayment} from '../models/payments/jobPayment.model';
import ClearState = Auth.ClearState;
import GetProvider = Payments.GetProvider;
import GetProviders = Payments.GetProviders;
import CreateProvider = Payments.CreateProvider;
import DeleteProvider = Payments.DeleteProvider;
import GetRegionPaymentProviders = Payments.GetRegionPaymentProviders;
import GetJobPayment = Payments.GetJobPayment;
import UpdateJobPaymentLocal = Payments.UpdateJobPaymentLocal;
import UpdateJobPaymentApi = Payments.UpdateJobPaymentApi;
import UpdateProvider = Payments.UpdateProvider;

export interface PaymentsStateModel {
  providers: Array<PaymentProviderConfiguration>;
  providerTypes: Array<string>;
  currentProvider: PaymentProviderConfiguration | null;
  jobPayment: JobPayment | null;
}

@State<PaymentsStateModel>({
  name: 'payments',
  defaults: {
    providers: [],
    providerTypes: [],
    currentProvider: null,
    jobPayment: null,
  }
})
@Injectable()
export class PaymentsState {
  constructor(private paymentsService: PaymentsService) {
  }

  @Selector()
  static providers(state: PaymentsStateModel): Array<PaymentProviderConfiguration> {
    return state.providers;
  }

  @Selector()
  static providerTypes(state: PaymentsStateModel): Array<string> {
    return state.providerTypes;
  }

  @Selector()
  static provider(state: PaymentsStateModel): PaymentProviderConfiguration {
    return state.currentProvider;
  }

  @Selector()
  static jobPayment(state: PaymentsStateModel): JobPayment {
    return state.jobPayment;
  }

  @Action(GetProviders)
  getProviders(ctx: StateContext<PaymentsStateModel>, action: GetProviders): Observable<Array<PaymentProviderConfiguration>> {
    const providers = ctx.getState().providers;

    if (!action.force && providers.length > 0) {
      return of(providers);
    }
    return this.paymentsService.getProviders().pipe(tap(result => {
      ctx.patchState({
        providers: result,
      });
    }));
  }

  @Action(GetProvider)
  getProvider(ctx: StateContext<PaymentsStateModel>, action: GetProvider): Observable<PaymentProviderConfiguration> {
    const provider = ctx.getState().currentProvider;
    if (provider && provider.id === action.id) {
      return of(provider);
    }
    const foundProvider = ctx.getState().providers.find(prvder => prvder.id === action.id);
    if (foundProvider) {
      ctx.patchState({
        currentProvider: foundProvider,
      });
      return of(foundProvider);
    }

    return this.paymentsService.getProvider(action.id).pipe(tap(result => {
      ctx.patchState({
        currentProvider: result,
      });
    }));
  }

  @Action(CreateProvider)
  createProvider(ctx: StateContext<PaymentsStateModel>, action: CreateProvider): Observable<PaymentProviderConfiguration> {
    return this.paymentsService.createProvider(action.provider).pipe(tap(result => {
      ctx.patchState({
        currentProvider: result,
      });
    }));
  }

  @Action(UpdateProvider)
  updateProvider(ctx: StateContext<PaymentsStateModel>, action: UpdateProvider): Observable<PaymentProviderConfiguration> {
    return this.paymentsService.updateProvider(action.provider).pipe(tap(result => {
      ctx.patchState({
        currentProvider: result,
      });
    }));
  }

  @Action(DeleteProvider)
  deleteProvider(ctx: StateContext<PaymentsStateModel>, action: DeleteProvider): Observable<PaymentProviderConfiguration> {
    const providers = ctx.getState().providers;
    const index = providers.indexOf(action.provider);
    return this.paymentsService.deleteProvider(action.provider).pipe(tap(result => {
      ctx.patchState({
        currentProvider: null,
        providers: [
          ...providers.slice(0, index),
          ...index === providers.length - 1 ? [] : providers.slice(index + 1)
        ]
      });
    }));
  }

  @Action(GetRegionPaymentProviders)
  getPaymentsProviderTypes(ctx: StateContext<PaymentsStateModel>, action: GetRegionPaymentProviders): Observable<Array<string>> {
    if (!action.force) {
      const providerTypes = ctx.getState().providerTypes;
      if (providerTypes.length > 0) {
        return of(providerTypes);
      }
    }
    return this.paymentsService.getPaymentsProviders().pipe(tap(result => {
      ctx.patchState({
        providerTypes: result
      });
    }));
  }

  @Action(GetJobPayment)
  getJobPayment(ctx: StateContext<PaymentsStateModel>, action: GetJobPayment): Observable<JobPayment> {
    if (!action.force) {
      const jobPayment = ctx.getState().jobPayment;
      if (jobPayment && jobPayment.id === action.id) {
        return of(jobPayment);
      }
    }
    return this.paymentsService.getJobPayment(action.id).pipe(tap(result => {
      ctx.patchState({
        jobPayment: result
      });
    }));
  }

  @Action(UpdateJobPaymentLocal)
  updateJobPaymentLocal(ctx: StateContext<PaymentsStateModel>, action: UpdateJobPaymentLocal): JobPayment {
    ctx.patchState({
      jobPayment: action.jobPayment
    });
    return action.jobPayment;
  }

  @Action(UpdateJobPaymentApi)
  updateJobPaymentApi(ctx: StateContext<PaymentsStateModel>, action: UpdateJobPaymentApi): Observable<JobPayment> {
    return this.paymentsService.updateJobPayment(action.id, action.jobPayment).pipe(tap(result => {
      ctx.patchState({
        jobPayment: result
      });
    }));
  }

  @Action(ClearState)
  clearState(ctx: StateContext<PaymentsStateModel>): void {
    ctx.setState({
      providers: [],
      providerTypes: [],
      currentProvider: null,
      jobPayment: null,
    });
  }
}
